import { apiClient as api } from "./httpClient";

// Marcos da linha do tempo. `kind` dirige ícone e cor; `isManual` separa o que alguém
// registrou do que o sistema deduziu do histórico.
export type TimelineKind =
  | "registration"
  | "specialty_start"
  | "evaluation"
  | "plan"
  | "sessions"
  | "absence"
  | "return"
  | "professional_change"
  | "frequency_change"
  | "inactive"
  | "outcome"
  /** Vínculo do paciente (RN-553f): uma ponta por marco — entrou / saiu */
  | "link";

export interface TimelineEvent {
  kind: TimelineKind;
  date: string;
  title: string;
  detail?: string | null;
  specialty?: string | null;
  professionalName?: string | null;
  isManual: boolean;
  outcomeId?: string | null;
  notes?: string | null;
  /** Bloco de sessões: detalhe mês a mês, aberto pelo "+" */
  children?: TimelineEvent[] | null;
}

export interface SpecialtyStatus {
  specialty: string;
  specialtyId?: string | null;
  active: boolean;
  outcomeName?: string | null;
  outcomeDate?: string | null;
}

export interface PatientTimelineSummary {
  firstContact?: string | null;
  totalAttendances: number;
  specialties: number;
  daysSinceLastAttendance?: number | null;
  specialtyStatus: SpecialtyStatus[];
  scheduled: number;
  noShows: number;
  cancelledByPatient: number;
  cancelledByProfessional: number;
  cancelledByClinic: number;
  /** sessões passadas sem presença marcada */
  withoutRecord: number;
  /** % das sessões que dependiam do paciente e ele fez */
  attendanceRate: number;
}

export interface PatientTimeline {
  patientId: string;
  patientName: string;
  summary: PatientTimelineSummary;
  events: TimelineEvent[];
  hasMore: boolean;
  monthsLoaded: number;
}

export interface TreatmentOutcomeType {
  id: string;
  name: string;
  /** Quando usar este tipo — aparece abaixo do campo ao selecionar */
  description?: string | null;
  isPositive: boolean;
  order: number;
  /** Falso em "Outro" e "Ocorrência": o marco entra na linha do tempo sem encerrar o
   *  tratamento na especialidade. Nesses tipos a descrição é obrigatória. */
  endsTreatment: boolean;
}

export interface TreatmentOutcomePayload {
  entityId: string;
  outcomeTypeId: string;
  outcomeDate: string; // yyyy-MM-dd
  professionalTypeId?: string | null;
  professionalRegistrationId?: string | null;
  notes?: string | null;
  /** Especialidade escrita à mão (opção "Outra"); só com professionalTypeId vazio */
  specialtyOther?: string | null;
}

export const patientTimelineService = {
  get: async (
    patientId: string,
    monthsBack?: number,
    sort?: "asc" | "desc",
    from?: string,
    to?: string
  ): Promise<PatientTimeline> => {
    const response = await api.get(`/patients/${patientId}/timeline`, {
      params: { monthsBack, sort, from: from || undefined, to: to || undefined },
    });
    return response.data;
  },

  outcomeTypes: async (): Promise<TreatmentOutcomeType[]> => {
    const response = await api.get("/patients/treatment-outcome-types");
    return response.data;
  },

  createOutcome: async (patientId: string, payload: TreatmentOutcomePayload): Promise<string> => {
    const response = await api.post(`/patients/${patientId}/treatment-outcomes`, payload);
    return response.data?.id;
  },

  deleteOutcome: async (outcomeId: string): Promise<void> => {
    await api.delete(`/patients/treatment-outcomes/${outcomeId}`);
  },
};
